import React from "react";
import classNames from "classnames";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { Edit as EditIcon, IkonateContext } from "react-ikonate";
import dayjs from "dayjs";
import LoadingPage from "shared/LoadingPage";
import Button from "shared/Button";
import ControlsHeader from "shared/ControlsHeader";
import TextInput from "shared/@forms/TextInput";
import TextArea from "shared/@forms/TextArea";
import DateTimePicker from "shared/@forms/DateTimePicker";
import CheckboxGroup from "shared/@forms/CheckboxGroup";

import styles from "./ContentEdit.module.css";
import { CMSData } from "../useCMSData";
import { useContentEdit } from "./useContentEdit";
import ImageUpload from "./ImageUpload";
import FileUpload from "./FileUpload";
import ShareOptions from "./ShareOptions";
import ContentSettings from "./ContentSettings";
import { FileList } from "./FileList/FileList";
import createNotification from "shared/@utils/createNotification";

export function ContentEdit() {
  const history = useHistory();
  const location = useLocation();
  const cms = CMSData.useContainer();
  const { id } = useParams();
  const [isSaving, setIsSaving] = React.useState(false);
  const [unpublishDateFollowsPublish, setUnpublishDateFollowsPublish] =
    React.useState(false);
  const [hasAttemptedTitleSave, setHasAttemptedTitleSave] =
    React.useState(false);
  const [hasAttemptedPublishedSave, setHasAttemptedPublishedSave] =
    React.useState(false);
  const {
    isLoading,
    content,
    methods,
    isChanged,

    original,

    setThumbData,
    setFileData,
    fileData,

    createContentMutation,
    updateContentMutation,
    archiveFileMutation,
    unarchiveFileMutation,
    deleteFileMutation,

    fileIdsToArchive,
    setFileIdsToArchive,
    fileIdsToUnArchive,
    setFileIdsToUnArchive,
    fileIdsToDelete,
    setFileIdsToDelete,
  } = useContentEdit({
    id,
    optimisticStatus: location.state?.status,
    isSaving,
  });

  if (isLoading || content == null || cms.isLoading || cms.settings == null) {
    return <LoadingPage />;
  }

  const hasTitle =
    typeof content.title === "string" && content.title.trim().length > 0;
  const unpublishPreviewDate = dayjs(content.publishOn).add(1, "week").toDate();
  const hasPublishableFile =
    Boolean(fileData) ||
    (content.files || []).some((file) => {
      if (file.deleted || fileIdsToDelete.includes(file.fileId)) {
        return false;
      }

      if (fileIdsToUnArchive.includes(file.fileId)) {
        return true;
      }

      return !file.archived && !fileIdsToArchive.includes(file.fileId);
    });

  console.log(content);
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <ControlsHeader title={content.title}>
          {isSaving && (
            <span className={styles.savingBadge} role="status">
              Saving changes...
            </span>
          )}
          <Button
            className={styles.saveButton}
            disabled={!isChanged || isSaving}
            purpose="save"
            onClick={async () => {
              if (isSaving) return;
              if (!hasTitle) {
                setHasAttemptedTitleSave(true);
                return;
              }
              if (content.status === 1 && !hasPublishableFile) {
                setHasAttemptedPublishedSave(true);
                return;
              }
              setIsSaving(true);

              try {
                if (id == null) {
                  const contentId = await createContentMutation.mutateAsync();
                  history.replace(`/marketing/cms/edit/${contentId}`);
                  return;
                }

                const replacementFileIds = fileData
                  ? content.files
                      .filter((file) => !file.archived && !file.deleted)
                      .map((file) => file.fileId)
                  : [];
                const deleteIds = fileIdsToDelete;
                const archiveIds = Array.from(
                  new Set([...fileIdsToArchive, ...replacementFileIds])
                ).filter((fileId) => !deleteIds.includes(fileId));
                const unarchiveIds = fileIdsToUnArchive.filter(
                  (fileId) =>
                    !archiveIds.includes(fileId) && !deleteIds.includes(fileId)
                );

                await Promise.all([
                  ...archiveIds.map((fileId) =>
                    archiveFileMutation.mutateAsync({
                      fileId,
                      contentId: content.id,
                    })
                  ),
                  ...unarchiveIds.map((fileId) =>
                    unarchiveFileMutation.mutateAsync({
                      fileId,
                      contentId: content.id,
                    })
                  ),
                  ...deleteIds.map((fileId) =>
                    deleteFileMutation.mutateAsync({
                      fileId,
                      contentId: content.id,
                    })
                  ),
                ]);
                await updateContentMutation.mutateAsync();
                setFileIdsToArchive([]);
                setFileIdsToUnArchive([]);
                setFileIdsToDelete([]);
              } catch (error) {
                createNotification({
                  type: "danger",
                  title: "Error",
                  message: error.message || "Failed to save content.",
                  autoDismiss: true,
                });
              } finally {
                setIsSaving(false);
              }
            }}
          >
            {isSaving ? (
              <span className={styles.saveSpinner} aria-hidden="true" />
            ) : (
              "Save"
            )}
          </Button>

          <Button
            className={styles.cancelButton}
            disabled={isSaving}
            onClick={() => {
              methods.set(JSON.parse(JSON.stringify(original)));
              history.goBack();
            }}
          >
            {isChanged ? "Cancel" : "Done"}
          </Button>
        </ControlsHeader>
      </div>

      <div className={styles.main}>
        {/* left side start */}
        <div className={classNames(styles.col, styles.section)}>
          <div className={styles.titleAndDescription}>
            <TextInput
              value={content.title}
              maxLength={150}
              required
              aria-invalid={hasAttemptedTitleSave && !hasTitle}
              onChange={(e) => {
                methods.setTitle(e.target.value);
              }}
              placeholder="Title (required)"
            />
            {hasAttemptedTitleSave && !hasTitle && (
              <span className={styles.titleError} role="alert">
                A title is required before saving.
              </span>
            )}

            <TextInput
              value={content.subTitle}
              onChange={(e) => {
                methods.setSubTitle(e.target.value);
              }}
              placeholder={"Sub Title (Digital Info System & Retail App Only)"}
              className={classNames(styles.input, styles.subTitle)}
            />

            <TextArea
              placeholder="Description"
              autoFocus={false}
              maxLength={500}
              className={styles.textArea}
              value={content.description}
              onChange={(e) => {
                methods.setDescription(e.target.value);
              }}
            />
          </div>

          <div className={styles.imageUpload}>
            <ImageUpload content={content} onFileChange={setThumbData}>
              <Button>
                <div className={styles.row}>
                  <IkonateContext.Provider value={{ size: 20, border: 1 }}>
                    <EditIcon />
                  </IkonateContext.Provider>
                  <div className={styles.editButton}>Edit</div>
                </div>
              </Button>
            </ImageUpload>
          </div>

          <div className={styles.publishStatus}>
            <span className={styles.publishStatusTitle}>Publish Status:</span>

            <div className={styles.publishStatusButtons}>
              {content.status === 3 ? (
                <Button className={styles.archived}>Archived</Button>
              ) : (
                <Button
                  className={styles.unselectedArchived}
                  onClick={() => {
                    methods.setPublishStatus(3);
                  }}
                >
                  Archived
                </Button>
              )}

              {content.status === 2 ? (
                <Button className={styles.review}>In Review</Button>
              ) : (
                <Button
                  className={styles.reviewUnselected}
                  onClick={() => {
                    methods.setPublishStatus(2);
                  }}
                >
                  In Review
                </Button>
              )}

              {content.status === 1 ? (
                <Button className={styles.published}>Published</Button>
              ) : (
                <Button
                  className={styles.publishedUnselected}
                  onClick={() => {
                    methods.setPublishStatus(1);
                  }}
                >
                  Published
                </Button>
              )}
            </div>
            {hasAttemptedPublishedSave &&
              content.status === 1 &&
              !hasPublishableFile && (
                <span className={styles.fileRequiredError} role="alert">
                  A file is required to publish. Add a file or change the status
                  before saving.
                </span>
              )}
          </div>

          <div className={styles.dates}>
            <div className={styles.dateCol}>
              <span className={styles.dateHeader}>Publish On</span>

              <DateTimePicker
                value={content.publishOn}
                onChange={(publishOn) => {
                  methods.setPublishOn(publishOn);
                  if (unpublishDateFollowsPublish && publishOn) {
                    methods.setUnPublishOn(
                      dayjs(publishOn).add(1, "week").toDate()
                    );
                  }
                }}
              />
            </div>

            <div className={styles.dateCol}>
              <span className={styles.dateHeader}>Unpublish On</span>

              <DateTimePicker
                disabled={content.unPublishOn == null}
                value={content.unPublishOn ?? unpublishPreviewDate}
                onChange={(unPublishOn) => {
                  setUnpublishDateFollowsPublish(false);
                  methods.setUnPublishOn(unPublishOn);
                }}
              />

              <div className={styles.unpublishPickerToggle}>
                <CheckboxGroup
                  id="enableUnPublishOn"
                  checked={content.unPublishOn != null}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setUnpublishDateFollowsPublish(true);
                      methods.setUnPublishOn(
                        dayjs(content.publishOn).add(1, "week").toDate()
                      );
                    } else {
                      setUnpublishDateFollowsPublish(false);
                      methods.setUnPublishOn(null);
                    }
                  }}
                >
                  Unpublish on this date
                </CheckboxGroup>
              </div>
            </div>
          </div>

          <FileUpload
            fileOptions={cms.settings.fileOptions}
            onFileChange={setFileData}
          />

          <FileList
            files={content.files}
            stagedFile={fileData}
            onClearStagedFile={() => {
              setFileData(null);
            }}
            onArchiveFile={(file) => {
              setFileIdsToArchive((fileIds) =>
                fileIds.includes(file.fileId)
                  ? fileIds
                  : [...fileIds, file.fileId]
              );
              setFileIdsToUnArchive((fileIds) =>
                fileIds.filter((fileId) => fileId !== file.fileId)
              );
              file.archived = true;
            }}
            onUnarchiveFile={(file) => {
              const activeFiles = content.files.filter(
                (currentFile) =>
                  !currentFile.archived &&
                  !currentFile.deleted &&
                  currentFile.fileId !== file.fileId
              );
              const activeFileIds = activeFiles.map(
                (activeFile) => activeFile.fileId
              );

              setFileIdsToArchive((fileIds) =>
                Array.from(
                  new Set([
                    ...fileIds.filter((fileId) => fileId !== file.fileId),
                    ...activeFileIds,
                  ])
                )
              );
              setFileIdsToUnArchive((fileIds) =>
                Array.from(
                  new Set([
                    ...fileIds.filter(
                      (fileId) => !activeFileIds.includes(fileId)
                    ),
                    file.fileId,
                  ])
                )
              );
              setFileIdsToDelete((fileIds) =>
                fileIds.filter((fileId) => fileId !== file.fileId)
              );
              activeFiles.forEach((activeFile) => {
                activeFile.archived = true;
              });
              file.archived = false;
            }}
            onRemoveArchived={(file) => {
              setFileIdsToDelete((fileIds) =>
                fileIds.includes(file.fileId)
                  ? fileIds
                  : [...fileIds, file.fileId]
              );
              setFileIdsToArchive((fileIds) =>
                fileIds.filter((fileId) => fileId !== file.fileId)
              );
              setFileIdsToUnArchive((fileIds) =>
                fileIds.filter((fileId) => fileId !== file.fileId)
              );
              file.deleted = true;
            }}
          />
        </div>
        {/* left side end */}

        {/* right side start */}
        <div
          className={classNames(
            styles.col,
            styles.section,
            styles.settingsSection
          )}
        >
          <ShareOptions content={content} methods={methods} />

          <ContentSettings
            settings={cms.settings}
            content={content}
            methods={methods}
          />
        </div>
      </div>
    </div>
  );
}
